import dayjs from "dayjs";

import { getPresetRange } from "pages/admin/components/CopilotDashboard/datePresets";

export const DEFAULT_PRESET = "this_month";

// Negative amounts (credits, adjustments) are kept and shown as "-$5.00", never clamped to zero.
export const formatMoney = (value) => {
  const n = Number(value ?? 0);
  return `${n < 0 ? "-" : ""}$${Math.abs(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// "+$1,200.00" / "-$80.00" / "$0.00" — a change is always current minus previous.
export const formatSignedMoney = (value) => {
  const n = Number(value ?? 0);
  return `${n > 0 ? "+" : ""}${formatMoney(n)}`;
};

// A percentage change as "+12.5%" / "-3.0%". The API sends null when the previous amount was zero, which means
// there is nothing to compare against ("no baseline") — not 0%.
export const formatPercentChange = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return null;
  const n = Number(value);
  return `${n > 0 ? "+" : ""}${n.toFixed(1)}%`;
};

// "$12.5k" / "$3.4M" for small spaces such as filter chips; whole dollars below $1,000.
export const formatMoneyCompact = (value) => {
  const n = Number(value ?? 0);
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `$${(n / 1_000).toFixed(abs >= 10_000 ? 0 : 1)}k`;
  return `$${Math.round(n)}`;
};

export const formatQuantity = (value) =>
  typeof value === "number" ? value.toLocaleString("en-US", { maximumFractionDigits: 2 }) : "—";

// A seat license (Copilot Business) is billed per user per month, so its quantity is a share of a month ("0.032258
// user-months"), which nobody can read. Inside one calendar month that share is turned into seat-days ("≈ 1 seat-day").
// It is an explanation only: GitHub bills a removed seat to the end of the cycle and prorates part-months, so the
// amount is always the reported one and is never worked out from this. Over several months the days differ, so the
// quantity stays in user-months. `range` is { from, to } as "YYYY-MM-DD".
const isUserMonths = (unitType) => /^user[-_ ]?months?$/i.test(String(unitType ?? "").trim());
export const describeQuantity = (quantity, unitType, range) => {
  if (typeof quantity !== "number") return { value: "—", unit: unitType ?? null, hint: null };
  if (!isUserMonths(unitType)) return { value: formatQuantity(quantity), unit: unitType ?? null, hint: null };
  const raw = `${quantity.toLocaleString("en-US", { maximumFractionDigits: 6 })} user-months`;
  const from = range?.from ? dayjs(range.from) : null;
  const to = range?.to ? dayjs(range.to) : null;
  const oneMonth = from?.isValid() && to?.isValid() && from.isSame(to, "month");
  const hint = `A seat license, billed per user per month and prorated for part-months. This is the share of a month (${raw}), not a number of people. GitHub may bill a removed seat to the end of the billing cycle.`;
  if (!oneMonth) {
    return { value: quantity.toLocaleString("en-US", { maximumFractionDigits: 3 }), unit: "user-months", hint };
  }
  const days = Math.round(quantity * from.daysInMonth() * 10) / 10;
  return { value: `≈ ${days.toLocaleString("en-US", { maximumFractionDigits: 1 })}`, unit: days === 1 ? "seat-day" : "seat-days", hint };
};

// "12%" of the whole, or a dash when there is nothing to divide by.
export const formatShare = (part, whole) => (whole > 0 ? `${Math.round((part / whole) * 100)}%` : "—");

// The x-axis / tooltip label: "17 Oct" for a day ("YYYY-MM-DD"), "Oct 2026" for a month ("YYYY-MM").
export const formatTrendDate = (value, granularity) => {
  if (!value) return "";
  return granularity === "month" ? dayjs(`${value}-01`).format("MMM YYYY") : dayjs(value).format("D MMM");
};

// The selected date range, from the URL when it has one, otherwise from the preset (default: this month).
// Always { preset, from, to } with from/to as "YYYY-MM-DD".
export const resolveRange = (searchParams) => {
  const from = searchParams.get("from");
  const to = searchParams.get("to");
  const preset = searchParams.get("preset");
  if (preset === "custom" || (!preset && from && to)) {
    if (from && to) return { preset: "custom", from, to };
  }
  const range = getPresetRange(preset && preset !== "custom" ? preset : DEFAULT_PRESET) ?? getPresetRange(DEFAULT_PRESET);
  return {
    preset: preset && preset !== "custom" ? preset : DEFAULT_PRESET,
    from: range.from.format("YYYY-MM-DD"),
    to: range.to.format("YYYY-MM-DD"),
  };
};

export const METRICS = [
  { key: "net_amount", label: "Net" },
  { key: "gross_amount", label: "Gross" },
  { key: "discount_amount", label: "Discount" },
  // Only offered when the API returns the Deloitte fields (see availableMetrics).
  { key: "deloitte_net_amount", label: "Deloitte net", deloitte: true },
  { key: "deloitte_discount_amount", label: "Deloitte discount", deloitte: true },
];

export const metricLabel = (key) => METRICS.find((m) => m.key === key)?.label ?? "Net";

// The Deloitte measures are left out when the API is an older one that does not return them.
export const availableMetrics = (hasDeloitteFields) => METRICS.filter((m) => !m.deloitte || hasDeloitteFields);

// The average spend per day over the range (a plain average, not a forecast).
export const getSpendInsights = (netTotal, range) => {
  const days = Math.max(dayjs(range.to).diff(dayjs(range.from), "day") + 1, 1);
  return { days, avgPerDay: (netTotal ?? 0) / days };
};

// The API only accepts a range where the start isn't after the end; the quality report also stops at 366 days.
export const QUALITY_MAX_DAYS = 366;
export const rangeDays = (range) => dayjs(range.to).diff(dayjs(range.from), "day") + 1;
export const rangeError = (range) => {
  if (!dayjs(range.from).isValid() || !dayjs(range.to).isValid()) return "Choose a valid start and end date.";
  if (dayjs(range.from).isAfter(dayjs(range.to), "day")) return "The start date is after the end date.";
  return null;
};

// Deloitte has one negotiated discount on everything it is billed: $0.0064 a credit against the $0.01 list price, which
// is 36% off. The rate is the central setting the Copilot dashboard uses, so the discount is worked out from it (and
// follows it if it changes) rather than being a second number to keep in step. It applies to the metered net amount,
// the part GitHub bills after its own discounts: Deloitte pays that less the discount.
export const LIST_CREDIT_RATE = 0.01;
export const deloitteFigures = (netAmount, rate) => {
  const net = Number(netAmount ?? 0);
  const pays = net * (Number(rate) / LIST_CREDIT_RATE);
  return { discount: net - pays, net: pays };
};
// The discount as a whole percent for display: 0.0064 -> 36.
export const deloittePercent = (rate) => Math.round((1 - Number(rate) / LIST_CREDIT_RATE) * 100);

// The API works the Deloitte figures out and puts them on every amount (deloitte_discount_amount, deloitte_net_amount).
// An older API does not, so the figures are worked out here from the net amount and the rate instead.
export const hasDeloitteFields = (amount) => typeof amount?.deloitte_net_amount === "number";
export const deloitteAmounts = (amount, rate) => {
  if (hasDeloitteFields(amount)) return { discount: amount.deloitte_discount_amount ?? 0, net: amount.deloitte_net_amount };
  if (rate === undefined) return null;
  return deloitteFigures(amount?.net_amount, rate);
};
// "36" for the label: the API's own percent when it sent one, otherwise worked out from the rate.
export const deloitteLabelPercent = (apiPercent, rate) => {
  if (typeof apiPercent === "number") return Number(apiPercent.toFixed(1));
  return rate === undefined ? undefined : deloittePercent(rate);
};

// Reshapes a grouped trend (one point per date and product / SKU) into one row per date with a column per group, for
// a stacked chart. Dates a group has no usage on are missing from the API, so they are filled with 0 here (the
// stack needs a value); a date with no usage at all stays absent. Series are ordered biggest first over the period.
// `labelOf(point)` names a group; `groupBy` is "product" or "sku" (a SKU series is keyed by product / SKU / unit).
export const buildStackedTrend = (points, groupBy, metric, labelOf = (p) => p.product ?? "") => {
  const series = new Map();
  const byDate = new Map();
  (points ?? []).forEach((point) => {
    const id = groupBy === "sku" ? `${point.product ?? ""}|${point.sku ?? ""}|${point.unit_type ?? ""}` : point.product ?? "";
    const amount = point[metric] ?? 0;
    const entry = series.get(id) ?? { id, product: point.product ?? null, sku: point.sku ?? null, unit: point.unit_type ?? null, label: labelOf(point), total: 0 };
    entry.total += amount;
    series.set(id, entry);
    const row = byDate.get(point.date) ?? { date: point.date };
    row[id] = (row[id] ?? 0) + amount;
    byDate.set(point.date, row);
  });
  const ordered = [...series.values()].sort((a, b) => b.total - a.total);
  const rows = [...byDate.values()]
    .sort((a, b) => String(a.date).localeCompare(String(b.date)))
    .map((row) => {
      const full = { ...row };
      let total = 0;
      ordered.forEach((s) => {
        full[s.id] = full[s.id] ?? 0;
        total += full[s.id];
      });
      return { ...full, total };
    });
  return { rows, series: ordered };
};

// Same palette order as the Copilot dashboard's model charts (Deloitte green among them). Products aren't a fixed
// list, so a product gets the colour for its position in the list the API returns; that list is sorted the same
// way everywhere, so a product keeps its colour across charts.
export const PRODUCT_COLORS = ["#26890d", "#0097D7", "#F28C00", "#7B5EA7", "#E2231A", "#1F7AA8", "#E87722", "#43B02A"];
export const OTHER_COLOR = "#999999";
const MAX_SLICES = 6;

// product id -> its chart colour, by position in the list the API returned (sorted by gross cost). The donut, the
// products table and its icons all use this one map, so a product looks the same everywhere on the page.
export const productColorMap = (products) =>
  Object.fromEntries((products ?? []).map((p, index) => [p.product, PRODUCT_COLORS[index % PRODUCT_COLORS.length]]));

// Slices for the share-of-cost donut: the biggest products by the chosen measure, the rest rolled into "Other".
// Products with nothing for that measure are left out. `labelOf` turns an id into its readable name.
export const buildProductShare = (products, metric, labelOf = (id) => id) => {
  const colors = productColorMap(products);
  const rows = (products ?? [])
    .map((p) => ({ id: p.product, name: labelOf(p.product), value: p[metric] ?? 0, color: colors[p.product] }))
    .filter((row) => row.value > 0)
    .sort((a, b) => b.value - a.value);
  if (rows.length <= MAX_SLICES) return rows;
  const top = rows.slice(0, MAX_SLICES - 1);
  const otherValue = rows.slice(MAX_SLICES - 1).reduce((sum, row) => sum + row.value, 0);
  return [...top, { id: null, name: "Other", value: otherValue, color: OTHER_COLOR }];
};
